import { Color, ColorString } from '@fe/core';
import { ChartPosition, LineStyle, Size } from '../util';
import { CustomAxisDrawFunction } from './axis';

export type ChartPoint = [number, number];
export type ChartMargin = {
    top: number;
    right: number;
    bottom: number;
    left: number;
};
export type ChartProps = {
    isDark: boolean;
    background: {
        color: Color;
    };
    text: {
        color: Color;
        fontName: string;
        baseSize: number;
    };
    margin: ChartMargin;
};

export type ScaleSpecification = {
    position: ChartPosition;
    axisLabels?: TextAxisLabels | CustomAxisLabels<CustomAxisDrawFunction<any>>;
    sizePixels?: number;
    hideWhenNoData?: boolean;
} & (
    | {
          autoScale: true;
      }
    | {
          autoScale?: false;
          min: number;
          max: number;
      }
);

export type TextAxisLabels = {
    type: 'text';
    label?: (value: number) => string;
    labelStep?: number | ((min: number, max: number, availableSpace: number) => number);
    bigTick?: number | ((min: number, max: number, availableSpace: number) => number);
    smallTick?: number | ((min: number, max: number, availableSpace: number) => number);
    color: string;
    bigTickColor?: string;
    smallTickColor?: string;
};

export type CustomAxisLabels<TDraw extends CustomAxisDrawFunction<any>> = {
    type: 'custom';
    draw: TDraw;
    args: AxisDrawArgs<TDraw>;
};

type AxisDrawArgs<TDraw> = TDraw extends CustomAxisDrawFunction<infer TArgs> ? TArgs : never;

export function customAxisLabels<TDraw extends CustomAxisDrawFunction<any>>(labels: CustomAxisLabels<TDraw>): CustomAxisLabels<TDraw> {
    return labels;
}

type LayerBase = {
    id: string;
    visible?: boolean;
    xAxisId: string;
    yAxisId: string;
    sourceId: string;
    filter?: (d: ChartFeature, index: number) => boolean;
};

type Layer<T> = LayerBase & T;
export type ChartLayerSpecification =
    | LineLayerSpecification
    | CircleLayerSpecification
    | RectangleLayerSpecification
    | GridLineLayerSpecification
    | AxisLabelsLayerSpecification
    | IconLayerSpecification
    | TextLayerSpecification
    | ShadeLineLayerSpecification
    | ShadeLayerSpecification
    | AxisIndicatorLayerSpecification
    | RasterLayerSpecification
    | IconGridLayerSpecification;

export type LineLayerSpecification = Layer<{
    type: 'line';
    paint: {
        color: Color;
        width: number;
        style?: LineStyle;
    };
}>;

export type CircleLayerSpecification = Layer<{
    type: 'circle';
    paint: {
        color: Color | ((item: ChartFeature) => Color);
        radius?: number;
        hollow?: boolean;
    };
}>;

export type RectangleLayerSpecification = Layer<{
    type: 'rectangle';
    paint: {
        fill: Color;
        outlieWidth?: number;
        outlineColor?: Color;
        text?: boolean;
        textColor?: Color;
    };
}>;

export type GridLineLayerSpecification = Layer<{
    type: 'grid-lines';
    layout: {
        axis: 'x' | 'y';
    };
    paint: {
        lineColor: Color;
        lineWidth: number;
        lineStyle?: LineStyle;
    };
}>;

export type AxisLabelsLayerSpecification = Layer<{
    type: 'axis-labels';
    layout: {
        axis: 'x' | 'y';
        position?: ChartPosition;
        allowOverlap?: boolean;
        getText: (item: ChartFeature) => string;
        fontSize?: number;
    };
    paint: {
        textColor: Color;
    };
}>;

export type IconLayerSpecification = Layer<{
    type: 'icon';
    layout: {
        getPosition?: (features: ChartFeature[]) => ChartFeature | null;
    };
    paint: {
        icon?: HTMLImageElement;
        size: Size;
        offset?: [number, number];
    };
}>;

export type TextLayerSpecification = Layer<{
    type: 'text';
    paint: {
        color: Color;
        textField: string;
    };
}>;

export type ShadeLineLayerSpecification = Layer<{
    type: 'shade-line';
    paint: {
        dayColor: Color;
        nightColor: Color;
        outlineColor: Color;
    };
}>;

export type ShadeLayerSpecification = Layer<{
    type: 'shade';
    paint?: {
        color?: ColorString;
    };
}>;

export type AxisIndicatorLayerSpecification = Layer<{
    type: 'axis-indicator';
    layout: {
        axis: 'x' | 'y';
        value: number | ((features: ChartFeature[]) => number);
        text: string | ((features: ChartFeature[]) => string);
        offset?: number;
    };
    paint: {
        textColor: Color;
        fillColor: Color;
    };
}>;

export type RasterLayerSpecification = Layer<{
    type: 'raster';
    colorRamp: Array<{ color: [number, number, number, number]; value: number }>;
    getValue: (value: number[])=>number,
    paint?: {
        opacity?: number;
    };
}>;

export type IconGridLayerSpecification = Layer<{
    type: 'icon-grid';
    sprite: CanvasImageSource;
    iconSize: [number, number];
    iconSpacing: [number, number];
    getIcon: (value: number[]) => { x: number; y: number; w: number; h: number; rotation?: number; tint?: Color };
}>;

export type DataSourceSpecification = ChartRasterDataSource | ChartVectorDataSourceSpecification;

export type ChartVectorDataSourceSpecification<TGeometry extends ChartGeometry = ChartGeometry, TProperties = any> = {
    type: 'vector';
    features: ChartFeature<TGeometry, TProperties>[];
};

export type ChartPointGeometry = {
    type: 'Point';
    coordinates: ChartPoint;
};

export type ChartLineGeometry = {
    type: 'Line';
    coordinates: ChartPoint[];
};

export type ChartPolygonGeometry = {
    type: 'Polygon';
    bbox?: [number, number, number, number];
    coordinates: ChartPoint[];
};

export type ChartShadeGeometry = {
    type: 'Shade';
    stops: ChartPoint[];
};

export type ChartGeometry = ChartPointGeometry | ChartPolygonGeometry | ChartLineGeometry | ChartShadeGeometry;

export type ChartFeature<TGeometry extends ChartGeometry = ChartGeometry, TProperties = any> = {
    id?: string;
    geometry: TGeometry;
    properties?: TProperties;
};

export type ChartRasterDataSource = {
    type: 'raster';
    bounds: [number, number, number, number];
    getValue: (x: number, y: number) => number[] | null;
};

export type ChartQueryFeature = {
    layer: ChartLayerSpecification;
    feature: ChartFeature;
};
